import moment from 'moment';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { usePet } from '../../modules';

import Transition from '../../utils/Transition';

const SEVERITY_DOT = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

function Notifications() {
  const history = useHistory();
  const { lastInventoryAlert } = useSelector(state => state.application);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  const [, getNotifications] = usePet('notifications', { limit: 5 }, {
    notify: true,
    callback: ({ data }) => setNotifications(data?.notifications ?? [])
  });

  const [, getUnreadCount] = usePet('unreadNotificationsCount', {}, {
    notify: false,
    callback: ({ data }) => setUnread(data ?? 0)
  });

  const [, markRead] = usePet('markNotificationRead', {}, {
    notify: false,
    manual: true
  });

  const [, markAllRead] = usePet('markAllNotificationsRead', {}, {
    notify: true,
    manual: true,
    callback: () => {
      getNotifications();
      getUnreadCount();
    }
  });

  // low-stock-alert/near-expiry-alert already show their own toast - this generic signal
  // just means "a notification changed somewhere", so refetch the bell's own data.
  useEffect(() => {
    if (!lastInventoryAlert) return;
    getNotifications();
    getUnreadCount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastInventoryAlert]);

  const openNotification = notification => {
    if (!notification.read) markRead({ id: notification._id });
    setDropdownOpen(false);
    if (notification.link) history.push(notification.link);
    getNotifications();
    getUnreadCount();
  };

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="relative inline-flex ml-3">
      <button
        ref={trigger}
        className={`w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition duration-150 rounded-full ${dropdownOpen && 'bg-gray-200'}`}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="sr-only">Notifications</span>
        <svg className="w-4 h-4" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <path className="fill-current text-gray-500" d="M6.5 0C2.91 0 0 2.462 0 5.5c0 1.075.37 2.074 1 2.922V12l2.699-1.542A7.454 7.454 0 006.5 11c3.59 0 6.5-2.462 6.5-5.5S10.09 0 6.5 0z" />
          <path className="fill-current text-gray-400" d="M16 9.5c0-.987-.429-1.897-1.147-2.639C14.124 10.348 10.66 13 6.5 13c-.103 0-.202-.018-.305-.021C7.231 13.617 8.556 14 10 14c.449 0 .886-.04 1.307-.11L15 16v-4h-.012C15.627 11.285 16 10.425 16 9.5z" />
        </svg>
        {
          unread > 0 &&
            <div className="absolute top-0 right-0 min-w-[0.9rem] h-[0.9rem] px-[3px] flex items-center justify-center bg-red-500 border-2 border-white rounded-full text-[0.6rem] leading-none text-white">
              {unread > 9 ? '9+' : unread}
            </div>
        }
      </button>

      <Transition
        className="origin-top-right z-10 absolute top-full right-0 -mr-48 sm:mr-0 min-w-80 bg-white border border-gray-200 py-1.5 rounded shadow-lg overflow-hidden mt-1"
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="flex items-center justify-between pt-1.5 pb-2 px-4">
            <div className="text-xs font-semibold text-gray-400 uppercase">Notifications</div>
            {
              unread > 0 &&
                <button className="text-xs font-medium text-indigo-500 hover:text-indigo-600" onClick={() => markAllRead()}>
                  Mark all read
                </button>
            }
          </div>
          <ul>
            {
              notifications.length > 0 ?
                notifications.map(notification => (
                  <li className="border-b border-gray-200 last:border-0" key={notification._id}>
                    <Link
                      className="flex items-start gap-2 py-2 px-4 hover:bg-gray-50"
                      to="#0"
                      onClick={() => openNotification(notification)}
                    >
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[notification.severity] ?? SEVERITY_DOT.info}`} />
                      <span className="block">
                        <span className={`block text-sm mb-1 ${notification.read ? 'text-gray-600' : 'font-semibold text-gray-800'}`}>
                          {notification.title}
                        </span>
                        <span className="block text-xs text-gray-500 mb-1">{notification.message}</span>
                        <span className="block text-xs font-medium text-gray-400">{moment(notification.createdOn).fromNow()}</span>
                      </span>
                    </Link>
                  </li>
                ))
                :
                <li className="py-6 text-center text-sm text-gray-500">You're all caught up.</li>
            }
          </ul>
        </div>
      </Transition>
    </div>
  )
}

export default Notifications;
